<template>
  <v-card>
    <v-card-title>
      <v-row no-gutters class="d-flex justify-center flex-row align-center">
        <span class="table-title">Vales-troca</span>
        <v-spacer />
        <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" @input="getFilterResult" />
        <v-col cols="2">
          <export-email-report-dialog
            button-text="Enviar relatório"
            title="Enviar relatório de pontuações"
            description="O relatório de pontuações é enviado, através de um e-mail, para a caixa de entrada dos endereços de e-mail adicionados abaixo. Um arquivo CSV ou XLSX contendo todas as informações detalhadas dos pontos é enviado em anexo ao e-mail."
            :exportReportFn="exportReport"
            async
            @file-format="getFileFormat"
          />
        </v-col>
        <v-col cols="2">
          <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Busque nome" />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        :headers="headers"
        :search="search"
        :query="query"
        :queryVariables="queryVariables"
        queryName="pointsVouchers"
        queryMainPropertyName="items"
      >
        <template v-slot:no-data>
          <voucher-empty-state v-if="showEmptyState" />
          <span v-else class="no-results-message"> Nenhum vale-troca encontrado com os filtros aplicados. </span>
        </template>
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <v-tooltip top max-width="250">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <router-link v-if="item.status !== 'FINISHED'" class="link" :to="{ path: link(item._id) }">
                      <span class="truncate">{{ item.title || '-' }}</span>
                    </router-link>
                    <span v-else class="truncate cursor-text">{{ item.title || '-' }}</span>
                  </span>
                </template>
                <span>{{ item.title || '-' }}</span>
              </v-tooltip>
            </td>

            <td>
              <v-tooltip top max-width="250">
                <template v-slot:activator="{ on, attrs }">
                  <span class="truncate cursor-text" v-bind="attrs" v-on="on">
                    {{ formatDiscountProduct(item) }}
                  </span>
                </template>
                <span>{{ formatDiscountProduct(item) }}</span>
              </v-tooltip>
            </td>
            <td>
              <v-tooltip top max-width="250">
                <template v-slot:activator="{ on, attrs }">
                  <span class="truncate cursor-text" v-bind="attrs" v-on="on">
                    {{ item.description || '-' }}
                  </span>
                </template>
                <span>{{ item.description }}</span>
              </v-tooltip>
            </td>
            <td>
              {{ item.points ? locale(item.points, 0) : '-' }}
            </td>
            <td>
              <mf-icon fill small :color="getPointsStatusLabel(item.status).color">fiber_manual_record</mf-icon>
              {{ getPointsStatusLabel(item.status).text }}
            </td>
            <td>
              {{
                item.initial_date && item.final_date
                  ? `${DateTime.fromISO(item.initial_date).setZone('UTC').toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(item.final_date)
                      .setZone('UTC')
                      .toFormat('dd/MM/yyyy')}`
                  : '-'
              }}
            </td>
            <td>
              {{ item.updated_at ? DateTime.fromISO(item.updated_at).setZone('UTC').toFormat('dd/MM/yyyy') : '-' }}
            </td>
            <td class="text-right">
              <v-menu :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                  <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
                </template>
                <v-list>
                  <v-list-item
                    v-if="item.status === 'DRAFT' || item.status === 'SCHEDULED' || item.status === 'ACTIVE'"
                    data-cy="editVoucher"
                    @click="$goTo(link(item._id))"
                  >
                    <v-list-item-title>Editar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'ACTIVE'" data-cy="finalizeVoucher" @click="finalizeVoucher(item._id)">
                    <v-list-item-title>Finalizar</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="cloneVoucher(item._id, item.title)">
                    <v-list-item-title>Duplicar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'DRAFT'" data-cy="deleteVoucher" @click="deleteVoucher(item._id)">
                    <v-list-item-title color="error" :loading="$wait.is('remove-campaign')">Deletar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'SCHEDULED'" data-cy="unscheduleVoucher" @click="unscheduleVoucher(item._id)">
                    <v-list-item-title>Suspender</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
      </paginated-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import truncate from 'lodash/truncate'
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import ExportEmailReportDialog from '@/components/export-email-report/ExportEmailReportDialog.vue'
import VoucherEmptyState from './VoucherEmptyState.vue'
import {
  MUTATION_RESEND_ASYNC_JOB,
  QUERY_GET_VOUCHERS,
  MUTATION_UNSCHEDULED_VOUCHER,
  MUTATION_FINALIZE_VOUCHER,
  MUTATION_CLONE_VOUCHER,
  MUTATION_DELETE_VOUCHER,
  QUERY_GET_USERS_TOTAL_POINTS,
} from '../../../graphql'
import { localeString, createCsvFromArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  components: {
    PaginatedDataTable,
    ExportEmailReportDialog,
    VoucherEmptyState,
  },
  filters: {
    hifen(value) {
      if (!value) return '-'
      return value
    },
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    search: '',
    showTooltipTitle: {},
    showTooltipDescription: {},
    showTooltipUnits: {},
    showTooltipMoney: {},
    locale: localeString,
    DateTime: DateTime,
    exportUsersPointsLoading: false,
    statusToSearch: [],
    fileFormat: 'CSV',
    totalVouchersCount: 0,
  }),
  computed: {
    parsedStatus() {
      return this.statusToSearch ? this.statusToSearch : []
    },
    query() {
      return QUERY_GET_VOUCHERS
    },
    queryVariables() {
      return {
        status: this.parsedStatus,
      }
    },
    itemsFilter() {
      return [
        { label: this.getPointsStatusLabel('ACTIVE').text, value: 'ACTIVE', color: this.getPointsStatusLabel('ACTIVE').color, active: true },
        { label: this.getPointsStatusLabel('SCHEDULED').text, value: 'SCHEDULED', color: this.getPointsStatusLabel('SCHEDULED').color, active: true },
        { label: this.getPointsStatusLabel('FINISHED').text, value: 'FINISHED', color: this.getPointsStatusLabel('FINISHED').color, active: true },
        { label: this.getPointsStatusLabel('DRAFT').text, value: 'DRAFT', color: this.getPointsStatusLabel('DRAFT').color, active: true },
      ]
    },
    headers() {
      return [
        {
          text: 'Nome',
          value: 'title',
          width: '15%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Desconto/Produto',
          value: 'product_description',
          width: '20%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '20%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Pontos',
          value: 'points',
          width: '10%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Status',
          value: 'status',
          width: '10%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Data de validade',
          value: 'initial_date',
          width: '15%',
          isActive: true,
          isDefault: true,
        },
        {
          text: 'Última atualização',
          value: 'updated_at',
          width: '10%',
          isActive: true,
          isDefault: true,
        },
        {
          text: '',
          value: 'menu',
          width: '5%',
          isActive: true,
          isDefault: true,
          sortable: false,
        },
      ]
    },
    showEmptyState() {
      return this.totalVouchersCount === 0
    },
  },
  apollo: {
    totalVouchers: {
      query: QUERY_GET_VOUCHERS,
      variables() {
        return {
          page: 1,
          pageSize: 1,
          status: [],
        }
      },
      fetchPolicy: 'no-cache',
      update({ pointsVouchers }) {
        this.totalVouchersCount = pointsVouchers?.records || 0
        return pointsVouchers
      },
    },
  },
  beforeMount() {
    this.statusToSearch = this.itemsFilter.map((item) => item.value)
  },
  methods: {
    getFileFormat(value) {
      this.fileFormat = value
    },
    async exportReport(emails) {
      return this.$apollo.mutate({
        mutation: MUTATION_RESEND_ASYNC_JOB,
        fetchPolicy: 'no-cache',
        variables: {
          emails,
          fileFormat: this.fileFormat,
        },
        context: {
          uri: this.$microservicesUrls['asyncJobs'],
        },
      })
    },
    getPointsStatusLabel(status) {
      switch (status?.toUpperCase()) {
        case 'INACTIVE':
          return {
            color: '#6B7580',
            text: 'Inativo',
          }
        case 'DRAFT':
          return {
            color: '#6B7580',
            text: 'Rascunho',
          }
        case 'SCHEDULED':
          return {
            color: '#EDB900',
            text: 'Agendado',
          }
        case 'FINISHED':
          return {
            color: '#0079F2',
            text: 'Concluído',
          }
        case 'ACTIVE':
          return {
            color: '#0DA740',
            text: 'Ativo',
          }
        case 'INDUSTRY_PROPOSAL':
          return {
            color: '#002040',
            text: 'Proposta Indústria',
          }
        case 'PAUSED':
          return {
            color: '#F15435',
            text: 'Suspenso',
          }
        case 'INDUSTRY_REJECTED':
          return {
            color: '#B3280D',
            text: 'Rejeitado',
          }
        case 'EXPIRED':
          return {
            color: '#F15435',
            text: 'Expirado',
          }
        case 'PROCESSING':
          return {
            color: '',
            text: 'Processando',
          }
        default:
          return {
            color: '#898989',
            text: 'Rascunho',
          }
      }
    },
    formatDiscountProduct(item) {
      if (item.type === 'PRODUCT' && item.product_description) {
        return item.product_description
      }

      if (item.money) {
        return `R$ ${this.locale(item.money, 2)}`
      }

      return '-'
    },
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    getFormatedText(text) {
      return truncate(text, {
        length: 24,
        separator: ' ',
      })
    },
    getMessageDates(date, time = null) {
      let messageData = ''
      let dataFormater = ''
      if (time) {
        messageData = ''.concat(date, ' ', time)
        dataFormater = 'DD/MM/YYYY | HH:mm'
      } else {
        messageData = date
        dataFormater = 'DD/MM/YYYY'
      }
      return date ? DateTime.fromISO(messageData).setZone('UTC').toFormat(dataFormater) : '-'
    },
    async getDataToExport() {
      try {
        const { data } = await this.$apollo.query({
          query: this.query,
          variables: {
            ...this.queryVariables,
            page: 1,
            pageSize: 1000,
            search: this.search,
            sortDirection: [],
            sort: [],
          },
          fetchPolicy: 'no-cache',
        })

        const items = data.pointsVouchers?.items || []

        const headers = this.headers.filter((header) => header.isActive !== false).map((h) => h.text)

        const rows = items.map((item) => {
          const row = []
          this.headers.forEach((header) => {
            if (header.isActive !== false) {
              switch (header.value) {
                case 'title':
                  row.push(item.title || '-')
                  break
                case 'product_description':
                  row.push(this.formatDiscountProduct(item))
                  break
                case 'description':
                  row.push(item.description || '-')
                  break
                case 'points':
                  row.push(item.points ? this.locale(item.points, 0) : '-')
                  break
                case 'status':
                  row.push(this.getPointsStatusLabel(item.status).text)
                  break
                case 'initial_date':
                  row.push(
                    item.initial_date && item.final_date
                      ? `${DateTime.fromISO(item.initial_date).setZone('UTC').toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(item.final_date)
                          .setZone('UTC')
                          .toFormat('dd/MM/yyyy')}`
                      : '-'
                  )
                  break
                case 'updated_at':
                  row.push(item.updated_at ? DateTime.fromISO(item.updated_at).setZone('UTC').toFormat('dd/MM/yyyy') : '-')
                  break
                default:
                  row.push(item[header.value] || '-')
              }
            }
          })
          return row
        })

        return { headers, rows }
      } catch (error) {
        console.error('Erro ao buscar dados para exportação:', error)
        throw error
      }
    },
    async cloneVoucher(id, name) {
      const confirmed = await this.$confirm({
        ...this.$t('campanhas.confirmations.clone'),
        message: `Deseja duplicar ${name ? 'o vale-troca ' + name.trim().bold() : 'este vale-troca'}?`,
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_CLONE_VOUCHER,
          variables: {
            id,
          },
        })
        .then(
          ({
            data: {
              cloneVoucher: { _id: newId },
            },
          }) => {
            this.$router.push(`/mobile/pontos/${newId}`).catch(() => {})
          }
        )
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
        })
    },
    async deleteVoucher(id) {
      const confirmed = await await this.$confirm({
        title: 'Remover campanha',
        message: 'Tem certeza que deseja remover esta campanha?',
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_DELETE_VOUCHER,
          variables: {
            id,
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.$refs.table.paginationLoading = false
    },
    async unscheduleVoucher(id) {
      const confirmed = await this.$confirm(this.$t('campanhas.confirmations.unschedule'))
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_UNSCHEDULED_VOUCHER,
          variables: {
            id,
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },
    async finalizeVoucher(id) {
      const confirmed = await this.$confirm({
        title: 'Finalizar vale-troca',
        message:
          'Finalizando esse vale-troca ele deixará de ser exibido no aplicativo, e portanto, não poderá receber novas ativações, mas não cancelará os que já foram ativados por consumidores.',
        confirmText: 'Finalizar',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_FINALIZE_VOUCHER,
          variables: {
            id,
          },
        })
        .then(() => {
          this.$store.commit('smsCountsChanged', true)
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },
    link(param) {
      return `/mobile/pontos/${param}`
    },
    async exportUsersPoints() {
      this.exportUsersPointsLoading = true

      try {
        const { data } = await this.$apollo.query({
          query: QUERY_GET_USERS_TOTAL_POINTS,
          fetchPolicy: 'no-cache',
        })

        const users = data.users.users.map((user) => ({
          cpf: user.document_no || '',
          points: user.points?.total || 0,
        }))
        this.exportUsersPointsLoading = false
        createCsvFromArray(users, 'Pontuações', ['CPF/CNPJ', 'Total de pontos'])
      } catch (error) {
        this.$snackbar({ message: 'Erro ao exportar .csv', snackbarColor: 'error' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.table-title {
  font-family: 'Lato';
  font-weight: 600;
  font-size: 18px;
  color: #4b525a;
}

.points__export-users-points {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.01em;
  color: $marca-0-l40 !important;
  text-transform: none;
  -webkit-text-decoration-line: none;
  text-decoration-line: none;
}

.truncate {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 150px;
}

.link {
  color: $marca-0-l40 !important;
  text-decoration: underline;
  cursor: pointer;
}

.link:hover {
  text-decoration: underline;
}
</style>
